// Tile constants (matches shared/src/index.ts const enum)
export const Tile = {
  Floor: 0,
  Wall: 1,
  Target: 2,
  Box: 3,
  BoxOnTarget: 4,
  Player: 5,
  PlayerOnTarget: 6,
} as const;

// Color themes
export interface Theme {
  name: string;
  background: string;
  floor: string;
  wall: string;
  wallShadow: string;
  target: string;
  box: string;
  boxOnTarget: string;
  player: string;
  playerEye: string;
  gridLine: string;
}

export const THEMES: Record<string, Theme> = {
  warehouse: {
    name: 'Warehouse',
    background: '#1a1a2e',
    floor: '#2a2a4a',
    wall: '#4a4a6a',
    wallShadow: '#3a3a5a',
    target: '#ff6b6b',
    box: '#c8a85c',
    boxOnTarget: '#6bcf6b',
    player: '#4ecdc4',
    playerEye: '#ffffff',
    gridLine: '#252545',
  },
  dark: {
    name: 'Dark',
    background: '#0d0d0d',
    floor: '#1a1a1a',
    wall: '#444444',
    wallShadow: '#333333',
    target: '#ff4757',
    box: '#ffa502',
    boxOnTarget: '#2ed573',
    player: '#3742fa',
    playerEye: '#ffffff',
    gridLine: '#151515',
  },
};

export class SokobanRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private theme: Theme;
  private tileSize = 0;
  private offsetX = 0;
  private offsetY = 0;
  private animating = false;
  private animationFrame = 0;
  private completionParticles: Array<{
    x: number; y: number; vx: number; vy: number;
    color: string; size: number; life: number;
  }> = [];

  constructor(canvas: HTMLCanvasElement, theme: Theme = THEMES.warehouse) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.theme = theme;
    this.resize();
  }

  resize() {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    this.ctx.scale(dpr, dpr);
  }

  setTheme(theme: Theme) {
    this.theme = theme;
  }

  render(grid: readonly (readonly number[])[], playerPos: { row: number; col: number }, highlightCells?: Set<string>) {
    const { ctx, theme } = this;
    const rows = grid.length;
    const cols = grid[0]?.length || 0;
    const canvasW = this.canvas.getBoundingClientRect().width;
    const canvasH = this.canvas.getBoundingClientRect().height;

    // Calculate tile size to fit grid in canvas with padding
    const padding = 16;
    const availW = canvasW - padding * 2;
    const availH = canvasH - padding * 2;
    this.tileSize = Math.floor(Math.min(availW / cols, availH / rows));
    this.tileSize = Math.min(this.tileSize, 64); // max tile size

    const gridW = cols * this.tileSize;
    const gridH = rows * this.tileSize;
    this.offsetX = Math.floor((canvasW - gridW) / 2);
    this.offsetY = Math.floor((canvasH - gridH) / 2);

    // Clear
    ctx.fillStyle = theme.background;
    ctx.fillRect(0, 0, canvasW, canvasH);

    // Draw grid
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const tile = grid[r][c];
        const x = this.offsetX + c * this.tileSize;
        const y = this.offsetY + r * this.tileSize;
        const s = this.tileSize;
        const inset = 1;

        // Floor for all non-wall tiles
        if (tile !== Tile.Wall) {
          ctx.fillStyle = theme.floor;
          ctx.fillRect(x + inset, y + inset, s - inset * 2, s - inset * 2);

          // Grid lines
          ctx.strokeStyle = theme.gridLine;
          ctx.lineWidth = 0.5;
          ctx.strokeRect(x + inset, y + inset, s - inset * 2, s - inset * 2);
        }

        switch (tile) {
          case Tile.Wall:
            this.drawWall(x, y, s, grid, r, c);
            break;
          case Tile.Target:
          case Tile.PlayerOnTarget:
            this.drawTarget(x, y, s);
            if (tile === Tile.PlayerOnTarget) this.drawPlayer(x, y, s);
            break;
          case Tile.Box:
            this.drawBox(x, y, s, false);
            break;
          case Tile.BoxOnTarget:
            this.drawTarget(x, y, s);
            this.drawBox(x, y, s, true);
            break;
          case Tile.Player:
            this.drawPlayer(x, y, s);
            break;
        }

        // Highlight hint cells
        if (highlightCells?.has(`${r},${c}`)) {
          ctx.fillStyle = 'rgba(255, 255, 100, 0.2)';
          ctx.fillRect(x + inset, y + inset, s - inset * 2, s - inset * 2);
        }
      }
    }

    // Draw completion particles
    if (this.completionParticles.length > 0) {
      this.drawParticles();
    }
  }

  private drawWall(x: number, y: number, s: number, grid: readonly (readonly number[])[], r: number, c: number) {
    const { ctx, theme } = this;

    // Main wall fill
    ctx.fillStyle = theme.wall;
    ctx.fillRect(x, y, s, s);

    // Brick rows: 3 rows of bricks with mortar lines
    const mortarW = Math.max(1, s * 0.04);
    const rowH = (s - mortarW * 4) / 3;
    const brickW = (s - mortarW * 3) / 2;

    ctx.fillStyle = 'rgba(0,0,0,0.2)';
    // Horizontal mortar lines
    for (let i = 0; i <= 3; i++) {
      ctx.fillRect(x, y + i * (rowH + mortarW), s, mortarW);
    }

    // Vertical mortar lines (offset per row)
    for (let row = 0; row < 3; row++) {
      const brickY = y + mortarW + row * (rowH + mortarW);
      const offsetFactor = ((r * 3 + row) % 2 === 0) ? 0 : brickW * 0.5;

      for (let bx = offsetFactor; bx < s; bx += brickW + mortarW) {
        ctx.fillRect(x + bx, brickY, mortarW, rowH);
      }

      // Brick face highlight (top-left) and shadow (bottom-right) per brick
      for (let bx = offsetFactor + mortarW; bx < s - mortarW; bx += brickW + mortarW) {
        const brickLeft = x + bx;
        const bw = Math.min(brickW, x + s - brickLeft);
        if (bw <= 0) continue;

        // Top highlight
        ctx.fillStyle = 'rgba(255,255,255,0.1)';
        ctx.fillRect(brickLeft, brickY, bw, Math.max(1, rowH * 0.15));
        // Left highlight
        ctx.fillRect(brickLeft, brickY, Math.max(1, bw * 0.08), rowH);
        // Bottom shadow
        ctx.fillStyle = 'rgba(0,0,0,0.12)';
        ctx.fillRect(brickLeft, brickY + rowH - Math.max(1, rowH * 0.15), bw, Math.max(1, rowH * 0.15));
        // Right shadow
        ctx.fillRect(brickLeft + bw - Math.max(1, bw * 0.08), brickY, Math.max(1, bw * 0.08), rowH);

        ctx.fillStyle = 'rgba(0,0,0,0.2)';
      }
    }

    // Overall top edge highlight
    ctx.fillStyle = 'rgba(255,255,255,0.06)';
    ctx.fillRect(x, y, s, 2);
    // Overall bottom edge shadow
    ctx.fillStyle = theme.wallShadow;
    ctx.fillRect(x, y + s - 2, s, 2);
  }

  private drawTarget(x: number, y: number, s: number) {
    const { ctx, theme } = this;
    const cx = x + s / 2;
    const cy = y + s / 2;
    const r = s * 0.22;

    // Outer glow
    ctx.fillStyle = theme.target;
    ctx.globalAlpha = 0.25;
    ctx.beginPath();
    ctx.moveTo(cx, cy - r * 1.6);
    ctx.lineTo(cx + r * 1.6, cy);
    ctx.lineTo(cx, cy + r * 1.6);
    ctx.lineTo(cx - r * 1.6, cy);
    ctx.closePath();
    ctx.fill();

    // Diamond shape
    ctx.globalAlpha = 0.8;
    ctx.fillStyle = theme.target;
    ctx.beginPath();
    ctx.moveTo(cx, cy - r * 1.2);
    ctx.lineTo(cx + r * 1.2, cy);
    ctx.lineTo(cx, cy + r * 1.2);
    ctx.lineTo(cx - r * 1.2, cy);
    ctx.closePath();
    ctx.fill();

    // Inner bright diamond
    ctx.globalAlpha = 1;
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.beginPath();
    ctx.moveTo(cx, cy - r * 0.5);
    ctx.lineTo(cx + r * 0.5, cy);
    ctx.lineTo(cx, cy + r * 0.5);
    ctx.lineTo(cx - r * 0.5, cy);
    ctx.closePath();
    ctx.fill();
  }

  private drawBox(x: number, y: number, s: number, onTarget: boolean) {
    const { ctx, theme } = this;
    const padding = s * 0.1;
    const bx = x + padding;
    const by = y + padding;
    const bs = s - padding * 2;
    const rad = 4;

    // Drop shadow
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    this.roundRect(bx + 2, by + 2, bs, bs, rad);

    // Box body — warmer golden color
    const baseColor = onTarget ? theme.boxOnTarget : '#d4a843';
    ctx.fillStyle = baseColor;
    this.roundRect(bx, by, bs, bs, rad);

    // 3D bevel — top/left highlight edges
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.fillRect(bx + rad, by + 1, bs - rad * 2, 3);     // top edge
    ctx.fillRect(bx + 1, by + rad, 3, bs - rad * 2);     // left edge

    // 3D bevel — bottom/right shadow edges
    ctx.fillStyle = 'rgba(0,0,0,0.2)';
    ctx.fillRect(bx + rad, by + bs - 3, bs - rad * 2, 3); // bottom edge
    ctx.fillRect(bx + bs - 3, by + rad, 3, bs - rad * 2); // right edge

    // Wood grain — horizontal lines
    ctx.strokeStyle = 'rgba(0,0,0,0.1)';
    ctx.lineWidth = 1;
    const grainSpacing = bs / 5;
    for (let i = 1; i < 5; i++) {
      const gy = by + i * grainSpacing;
      ctx.beginPath();
      ctx.moveTo(bx + 3, gy);
      ctx.lineTo(bx + bs - 3, gy);
      ctx.stroke();
    }

    // Cross marks on the crate
    ctx.strokeStyle = 'rgba(0,0,0,0.15)';
    ctx.lineWidth = 2;
    const cx = bx + bs / 2;
    const cy = by + bs / 2;
    const cr = bs * 0.25;
    ctx.beginPath();
    ctx.moveTo(cx - cr, cy - cr);
    ctx.lineTo(cx + cr, cy + cr);
    ctx.moveTo(cx + cr, cy - cr);
    ctx.lineTo(cx - cr, cy + cr);
    ctx.stroke();

    // Checkmark for box on target — more prominent
    if (onTarget) {
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth = Math.max(3, s * 0.06);
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(cx - cr * 0.6, cy);
      ctx.lineTo(cx - cr * 0.1, cy + cr * 0.5);
      ctx.lineTo(cx + cr * 0.7, cy - cr * 0.4);
      ctx.stroke();
      ctx.lineCap = 'butt';
    }
  }

  private drawPlayer(x: number, y: number, s: number) {
    const { ctx, theme } = this;
    const cx = x + s / 2;
    const cy = y + s / 2;

    // === Warehouse worker character ===
    const bodyW = s * 0.38;
    const bodyH = s * 0.32;
    const headR = s * 0.16;
    const bodyTop = cy - bodyH * 0.15;
    const bodyBot = bodyTop + bodyH;

    // Drop shadow
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    ctx.beginPath();
    ctx.ellipse(cx + 1, bodyBot + 2, bodyW * 0.5, s * 0.06, 0, 0, Math.PI * 2);
    ctx.fill();

    // Body — rounded rectangle torso
    ctx.fillStyle = theme.player;
    this.roundRect(cx - bodyW / 2, bodyTop, bodyW, bodyH, 4);

    // Body highlight (left side)
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    ctx.fillRect(cx - bodyW / 2 + 2, bodyTop + 2, bodyW * 0.3, bodyH - 4);

    // Arms — small bumps on sides
    const armW = s * 0.08;
    const armH = bodyH * 0.55;
    const armY = bodyTop + bodyH * 0.15;
    // Left arm
    ctx.fillStyle = theme.player;
    this.roundRect(cx - bodyW / 2 - armW + 1, armY, armW, armH, 2);
    // Right arm
    this.roundRect(cx + bodyW / 2 - 1, armY, armW, armH, 2);

    // Head
    const headCy = bodyTop - headR * 0.6;
    ctx.fillStyle = theme.player;
    ctx.beginPath();
    ctx.arc(cx, headCy, headR, 0, Math.PI * 2);
    ctx.fill();

    // Head highlight
    ctx.fillStyle = 'rgba(255,255,255,0.2)';
    ctx.beginPath();
    ctx.arc(cx - headR * 0.2, headCy - headR * 0.15, headR * 0.55, 0, Math.PI * 2);
    ctx.fill();

    // Hard hat — orange/yellow safety cap
    ctx.fillStyle = '#f0a030';
    ctx.beginPath();
    ctx.ellipse(cx, headCy - headR * 0.55, headR * 1.15, headR * 0.45, 0, Math.PI, Math.PI * 2);
    ctx.fill();
    // Hat brim
    ctx.fillStyle = '#e89520';
    ctx.fillRect(cx - headR * 1.2, headCy - headR * 0.58, headR * 2.4, Math.max(2, s * 0.04));

    // Eyes
    const eyeR = headR * 0.17;
    const eyeOffX = headR * 0.38;
    const eyeY = headCy + headR * 0.1;
    ctx.fillStyle = theme.playerEye;
    ctx.beginPath();
    ctx.arc(cx - eyeOffX, eyeY, eyeR, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx + eyeOffX, eyeY, eyeR, 0, Math.PI * 2);
    ctx.fill();

    // Pupils
    ctx.fillStyle = '#333';
    const pupilR = eyeR * 0.5;
    ctx.beginPath();
    ctx.arc(cx - eyeOffX, eyeY, pupilR, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx + eyeOffX, eyeY, pupilR, 0, Math.PI * 2);
    ctx.fill();
  }

  private roundRect(x: number, y: number, w: number, h: number, r: number) {
    const { ctx } = this;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();
  }

  // Completion celebration
  startCelebration() {
    const canvasW = this.canvas.getBoundingClientRect().width;
    const canvasH = this.canvas.getBoundingClientRect().height;
    const colors = ['#ff6b6b', '#feca57', '#48dbfb', '#ff9ff3', '#54a0ff', '#5f27cd'];

    for (let i = 0; i < 60; i++) {
      this.completionParticles.push({
        x: canvasW / 2,
        y: canvasH / 2,
        vx: (Math.random() - 0.5) * 12,
        vy: (Math.random() - 0.5) * 12 - 4,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 6 + 2,
        life: 1,
      });
    }
  }

  private drawParticles() {
    const { ctx } = this;
    this.completionParticles = this.completionParticles.filter(p => {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.2; // gravity
      p.life -= 0.015;

      if (p.life <= 0) return false;

      ctx.globalAlpha = p.life;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      return true;
    });
  }

  // Get tile position from screen coordinates
  screenToGrid(screenX: number, screenY: number): { row: number; col: number } | null {
    const col = Math.floor((screenX - this.offsetX) / this.tileSize);
    const row = Math.floor((screenY - this.offsetY) / this.tileSize);
    if (row < 0 || col < 0) return null;
    return { row, col };
  }

  getTileSize() { return this.tileSize; }
  getOffset() { return { x: this.offsetX, y: this.offsetY }; }
}
